import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import PageLayout from '../components/layout/PageLayout';
import PageHeader from '../components/layout/PageHeader';
import ProviderCard from '../components/directory/ProviderCard';
import PostCard from '../components/community/PostCard';
import EventCard from '../components/community/EventCard';
import { Loading, ProviderGridSkeleton } from '../components/layout/Skeletons';
import { useSearch } from '../hooks/useSearch';

/**
 * The four groups, in the order somebody scans them, and what each one leads
 * to once the answer turns out to be in it.
 *
 * The page is a signpost, not a fifth listing: it shows enough of each group
 * to see where the answer is, then hands the reader to the page built to
 * browse that group, with its filters and its pagination. Growing them here
 * would mean maintaining all of it twice.
 */
const GROUPS = [
  {
    key: 'services',
    title: 'Services',
    to: '/services',
    more: (n) => `Voir les ${n} services`,
    grid: true,
  },
  {
    key: 'commerces',
    title: 'Commerces',
    to: '/commerces',
    more: (n) => `Voir les ${n} commerces`,
    grid: true,
  },
  {
    key: 'posts',
    title: 'Le fil du quartier',
    to: '/quartier',
    more: (n) => `Voir les ${n} publications`,
    grid: false,
  },
  {
    key: 'events',
    title: 'L’agenda',
    to: '/agenda',
    more: (n) => `Voir les ${n} événements`,
    grid: false,
  },
];

function Card({ groupKey, item }) {
  if ('posts' === groupKey) {
    return <PostCard post={item} />;
  }

  if ('events' === groupKey) {
    return <EventCard event={item} />;
  }

  return <ProviderCard provider={item} />;
}

/**
 * What one word finds across the whole hub.
 *
 * Somebody who types "boulangerie" does not know, and should not have to know,
 * whether the answer is a listing, a post or an event — the sections are the
 * hub's vocabulary, not theirs. So the search asks all four and says where the
 * answer is.
 *
 * An empty group is not shown. That is the opposite of the rule inside a card,
 * where an empty band keeps its room: here the room would say "nothing here"
 * four times over, which is noise, not alignment.
 */
function Search() {
  const { term, results, total, loading, error, setTerm } = useSearch();
  const [draft, setDraft] = useState(term);

  // Following the URL, so arriving from the home page or pressing back fills
  // the field with what is actually being searched.
  useEffect(() => {
    setDraft(term);
  }, [term]);

  const submit = (event) => {
    event.preventDefault();
    setTerm(draft);
  };

  const found = GROUPS.filter((group) => (results[group.key]?.items || []).length > 0);

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          reserveLede
          title="Rechercher"
          lede="Les services, les commerces, le fil du quartier et l’agenda, d’un seul mot."
        />

        <Paper
          component="form"
          variant="outlined"
          onSubmit={submit}
          sx={{ p: { xs: 2.5, md: 3 }, mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}
        >
          <TextField
            id="search-term"
            label="Que cherchez-vous ?"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Un métier, un commerce, un mot-clé"
            sx={{ flexGrow: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="contained" size="large" sx={{ flexShrink: 0, height: 56 }}>
            Chercher
          </Button>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Loading label="Recherche en cours">
            <ProviderGridSkeleton count={3} />
          </Loading>
        )}

        {!loading && !error && term.length < 2 && (
          <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
            <Typography variant="body1">
              Tapez au moins deux lettres&nbsp;: un métier, le nom d’un commerce, ou ce que vous
              cherchez entre voisins.
            </Typography>
          </Paper>
        )}

        {!loading && !error && term.length >= 2 && (
          <>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {0 === total
                ? `Aucun résultat pour « ${term} ».`
                : `${total} résultat${1 === total ? '' : 's'} pour « ${term} ».`}
            </Typography>

            {0 === total && (
              <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography variant="h4" component="p" sx={{ mb: 1 }}>
                  Rien trouvé
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Essayez un mot plus court, ou parcourez les rubriques&nbsp;: tout n’est pas
                  toujours écrit avec les mêmes mots.
                </Typography>
                {/* A gap rather than `spacing`: spacing is a left margin, so
                    once the buttons wrap on a phone they touch vertically. */}
                <Stack direction="row" sx={{ justifyContent: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                  <Button component={RouterLink} to="/services" variant="outlined">
                    Les services
                  </Button>
                  <Button component={RouterLink} to="/commerces" variant="outlined">
                    Les commerces
                  </Button>
                  <Button component={RouterLink} to="/quartier" variant="outlined">
                    Le fil
                  </Button>
                </Stack>
              </Paper>
            )}

            {found.map((group) => {
              const { items, total: groupTotal } = results[group.key];

              return (
                <Box component="section" key={group.key} sx={{ mb: 5 }}>
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 2.5 }}
                  >
                    <Typography variant="h3" component="h2">
                      {group.title}
                      <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                        {groupTotal}
                      </Box>
                    </Typography>

                    {/* The search is a signpost: past the first few, the page
                        that browses this group does it better. */}
                    {groupTotal > items.length && (
                      <Button
                        component={RouterLink}
                        to={`${group.to}?q=${encodeURIComponent(term)}`}
                        size="small"
                      >
                        {group.more(groupTotal)}
                      </Button>
                    )}
                  </Stack>

                  <Box
                    sx={
                      group.grid
                        ? {
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: 'minmax(0, 1fr)',
                              sm: 'repeat(2, minmax(0, 1fr))',
                              md: 'repeat(3, minmax(0, 1fr))',
                            },
                            gap: 3,
                          }
                        : { display: 'flex', flexDirection: 'column', gap: 2.25 }
                    }
                  >
                    {items.map((item) => (
                      <Card key={item.uuid} groupKey={group.key} item={item} />
                    ))}
                  </Box>

                  {/* Again under the cards, where the reader is once they
                      have read them: the link beside the title alone was
                      missed, and the list read as if it ended there. */}
                  {groupTotal > items.length && (
                    <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        component={RouterLink}
                        to={`${group.to}?q=${encodeURIComponent(term)}`}
                        variant="outlined"
                      >
                        {group.more(groupTotal)}
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Box>
    </PageLayout>
  );
}

export default Search;
